import React, { useState } from 'react';
import { StudySummary, StudyStatus, SLAStatus, Identity } from '../types';
import { Clock, ShieldAlert, Lock, User, FileText, Activity, AlertCircle, CheckCircle2, ChevronRight, Search, Filter, XCircle } from 'lucide-react';

interface WorklistProps {
  studies: StudySummary[];
  /** Surfaced rather than swallowed: an empty queue and a refused request look identical. */
  error?: string | null;
  identity: Identity;
  onSelectStudy: (id: string) => void;
  onRefresh: () => void;
  /** How many studies match, across every page — `studies` is one page of them (§13.6 R8). */
  total?: number;
}

export const Worklist: React.FC<WorklistProps> = ({
  studies,
  error,
  identity,
  onSelectStudy,
  onRefresh,
  total,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const filteredStudies = studies.filter(s => {
    const matchesStatus = selectedStatus === 'ALL' || s.status === selectedStatus;
    const matchesSearch = s.patient_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          s.patient_ref.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          s.id.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getSLABadge = (status: SLAStatus) => {
    switch (status) {
      case 'OVERDUE':
        return (
          <span className="inline-flex items-center space-x-1 bg-rose-950/80 border border-rose-800 text-rose-300 text-xs px-2.5 py-1 rounded-full font-medium">
            <AlertCircle className="h-3 w-3 text-rose-400" />
            <span>OVERDUE</span>
          </span>
        );
      case 'AT_RISK':
        return (
          <span className="inline-flex items-center space-x-1 bg-amber-950/80 border border-amber-800 text-amber-300 text-xs px-2.5 py-1 rounded-full font-medium animate-pulse-subtle">
            <Clock className="h-3 w-3 text-amber-400" />
            <span>AT RISK (&lt;12h)</span>
          </span>
        );
      case 'ON_TRACK':
        return (
          <span className="inline-flex items-center space-x-1 bg-emerald-950/80 border border-emerald-800 text-emerald-300 text-xs px-2.5 py-1 rounded-full font-medium">
            <CheckCircle2 className="h-3 w-3 text-emerald-400" />
            <span>ON TRACK</span>
          </span>
        );
      case 'STALLED':
        // Deliberately not a countdown badge: this study has stopped, and no deadline it could be
        // "on track" for still applies. It previously fell through to the COMPLETED badge below,
        // which said the opposite of what had happened (§13.6 Q5).
        return (
          <span className="inline-flex items-center space-x-1 bg-slate-800 border border-rose-900 text-rose-300 text-xs px-2.5 py-1 rounded-full font-medium">
            <XCircle className="h-3 w-3 text-rose-400" />
            <span>STALLED</span>
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center space-x-1 bg-slate-800 text-slate-400 text-xs px-2.5 py-1 rounded-full font-medium">
            <span>COMPLETED</span>
          </span>
        );
    }
  };

  const getStatusBadge = (status: StudyStatus) => {
    const styles: Record<StudyStatus, string> = {
      INGESTING: 'bg-cyan-950 border-cyan-800 text-cyan-300',
      INGEST_FAILED: 'bg-rose-950 border-rose-800 text-rose-300',
      AWAITING_ANALYSIS: 'bg-slate-800 border-slate-600 text-slate-300',
      ANALYZING: 'bg-indigo-950 border-indigo-800 text-indigo-300',
      ANALYSIS_FAILED: 'bg-rose-950 border-rose-800 text-rose-300',
      READY_FOR_REVIEW: 'bg-emerald-950 border-emerald-800 text-emerald-300',
      IN_REVIEW: 'bg-amber-950 border-amber-800 text-amber-300',
      PENDING_SIGNATURE: 'bg-purple-950 border-purple-800 text-purple-300',
      SIGNED: 'bg-slate-900 border-slate-700 text-slate-300',
      AMENDED: 'bg-rose-950 border-rose-800 text-rose-300'
    };

    return (
      <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-md border ${styles[status]}`}>
        {status.replace(/_/g, ' ')}
      </span>
    );
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
      {/* Top Banner Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="glass-panel p-5 rounded-2xl flex items-center space-x-4">
          <div className="h-12 w-12 rounded-xl bg-cyan-950/80 border border-cyan-800/50 flex items-center justify-center text-cyan-400">
            <Activity className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-400">Total Studies</p>
            {/* `total` counts every matching study; `studies` is one page of them (§13.6 R8).
                The other three tiles below are honest about being page-scoped only because they
                describe what is on screen — this one claimed to be a total and was not. */}
            <p className="text-2xl font-bold text-white mono">{total ?? studies.length}</p>
            {total !== undefined && total > studies.length && (
              <p className="text-[11px] text-slate-500">showing the {studies.length} due soonest</p>
            )}
          </div>
        </div>

        <div className="glass-panel p-5 rounded-2xl flex items-center space-x-4">
          <div className="h-12 w-12 rounded-xl bg-emerald-950/80 border border-emerald-800/50 flex items-center justify-center text-emerald-400">
            <Clock className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-400">Ready for Review</p>
            <p className="text-2xl font-bold text-white mono">
              {studies.filter(s => s.status === 'READY_FOR_REVIEW').length}
            </p>
          </div>
        </div>

        <div className="glass-panel p-5 rounded-2xl flex items-center space-x-4">
          <div className="h-12 w-12 rounded-xl bg-amber-950/80 border border-amber-800/50 flex items-center justify-center text-amber-400">
            <Lock className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-400">Active Locks</p>
            <p className="text-2xl font-bold text-white mono">
              {studies.filter(s => s.is_locked).length}
            </p>
          </div>
        </div>

        <div className="glass-panel p-5 rounded-2xl flex items-center space-x-4">
          <div className="h-12 w-12 rounded-xl bg-rose-950/80 border border-rose-800/50 flex items-center justify-center text-rose-400">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <div>
            <p className="text-xs font-medium text-slate-400">At Risk / Overdue</p>
            <p className="text-2xl font-bold text-white mono">
              {studies.filter(s => s.sla_status === 'AT_RISK' || s.sla_status === 'OVERDUE').length}
            </p>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="glass-panel p-4 rounded-2xl flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center space-x-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0">
          <Filter className="h-4 w-4 text-slate-400 ml-2 mr-1 shrink-0" />
          {['ALL', 'READY_FOR_REVIEW', 'IN_REVIEW', 'PENDING_SIGNATURE', 'SIGNED'].map(status => (
            <button
              key={status}
              onClick={() => setSelectedStatus(status)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-all whitespace-nowrap ${
                selectedStatus === status
                  ? 'bg-cyan-600 text-white shadow-md shadow-cyan-600/30'
                  : 'bg-slate-900 text-slate-400 hover:text-slate-200 border border-slate-800'
              }`}
            >
              {status === 'ALL' ? 'All Queue' : status.replace(/_/g, ' ')}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-72">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search patient, ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 text-slate-200 text-xs rounded-xl pl-9 pr-4 py-2 focus:outline-none focus:border-cyan-500 transition-all"
          />
        </div>
      </div>

      {/* Studies List Queue */}
      <div className="space-y-3">
        {filteredStudies.length === 0 ? (
          <div className="glass-panel p-12 text-center rounded-2xl space-y-3">
            <Activity className="h-10 w-10 text-slate-600 mx-auto" />
            <p className="text-slate-400 font-medium text-sm">No ECG studies found matching filters.</p>
          </div>
        ) : (
          filteredStudies.map((study) => (
            <div
              key={study.id}
              onClick={() => onSelectStudy(study.id)}
              className="glass-panel glass-panel-hover p-5 rounded-2xl cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-4 group"
            >
              {/* Patient & Device Meta */}
              <div className="flex items-start space-x-4">
                <div className="h-11 w-11 rounded-xl bg-slate-900 border border-slate-800 flex items-center justify-center text-cyan-400 shrink-0 group-hover:border-cyan-500/40 transition-all">
                  <FileText className="h-5 w-5" />
                </div>

                <div>
                  <div className="flex items-center space-x-3">
                    <h3 className="text-base font-bold text-white group-hover:text-cyan-300 transition-colors">
                      {study.patient_name}
                    </h3>
                    <span className="mono text-xs text-slate-400 bg-slate-900 px-2 py-0.5 rounded border border-slate-800">
                      {study.patient_ref}
                    </span>
                    {/* The study's own reference, not its database UUID (DESIGN.md §13.6 R8).
                        A UUID is not what anyone says on the phone about a study, and it was
                        occupying the most prominent slot on every row. */}
                    <span className="mono text-xs text-cyan-400 bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-800/50">
                      {study.reference}
                    </span>
                    {/* Whose patient this is. Under delegation a reviewer moves between tenants
                        mid-queue, and mistaking that has consequences (§7.2) — the field was in
                        the API and rendered nowhere. */}
                    {study.tenant_name && (
                      <span className="text-xs text-slate-300 bg-slate-800/80 px-2 py-0.5 rounded border border-slate-700">
                        {study.tenant_name}
                      </span>
                    )}
                  </div>

                  <p className="text-xs text-slate-400 mt-1 flex items-center gap-3">
                    {study.status === 'INGESTING' ? (
                      <span className="text-cyan-400">Ingesting upload…</span>
                    ) : study.status === 'INGEST_FAILED' ? (
                      <span className="text-rose-400">{study.ingest_error || 'Ingest failed'}</span>
                    ) : (
                      <>
                        <span>{study.device_model}</span>
                        <span>•</span>
                        <span>{study.num_channels} Channel ({study.sample_rate_hz?.toFixed(1)} Hz)</span>
                        <span>•</span>
                        <span>{((study.duration_seconds ?? 0) / 86400).toFixed(1)} Days Wear</span>
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Status, Lock & SLA Badges */}
              <div className="flex items-center space-x-4 justify-between md:justify-end">
                {/* Lock Status */}
                {study.is_locked ? (
                  /*
                   * The server does not name another holder, so neither does this: revealing
                   * which clinician at which tenant is reviewing a patient is itself a
                   * disclosure, and the lock only needs to say "not yours right now".
                   */
                  <div
                    className={`flex items-center space-x-1.5 text-xs px-3 py-1 rounded-lg font-medium border ${
                      study.locked_by_self
                        ? 'bg-emerald-950/90 border-emerald-800 text-emerald-300'
                        : 'bg-amber-950/90 border-amber-800 text-amber-300'
                    }`}
                  >
                    <Lock
                      className={`h-3.5 w-3.5 ${
                        study.locked_by_self ? 'text-emerald-400' : 'text-amber-400'
                      }`}
                    />
                    <span>{study.locked_by_self ? 'Locked by you' : 'In review'}</span>
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    <User className="h-3.5 w-3.5" />
                    <span>Available</span>
                  </div>
                )}

                {/* Status & SLA */}
                <div className="flex items-center space-x-2">
                  {getStatusBadge(study.status)}
                  {getSLABadge(study.sla_status)}
                </div>

                <ChevronRight className="h-5 w-5 text-slate-500 group-hover:text-cyan-400 group-hover:translate-x-1 transition-all" />
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
